import { createTwoFilesPatch } from 'diff';
import { MapStore } from 'nanostores';
import { EditorDocuments } from '~/stores/workbench';
import { MODIFICATIONS_TAG_NAME } from '~/utils/constants';

export const modificationsRegex = new RegExp(
  `^<${MODIFICATIONS_TAG_NAME}>[\\s\\S]*?<\\/${MODIFICATIONS_TAG_NAME}>\\s+`,
  'g'
);

interface ModifiedFile {
  type: 'diff' | 'file';
  content: string;
}

export type FileModifications = Record<string, ModifiedFile>;

/**
 * Computes a diff in the unified format. The only difference is that the header is omitted
 * because it will always assume that you're comparing two versions of the same file and
 * it allows us to avoid the extra characters we send back to the llm.
 *
 * @see https://www.gnu.org/software/diffutils/manual/html_node/Unified-Format.html
 */
export function diffFiles(fileName: string, oldFileContent: string, newFileContent: string) {
  let unifiedDiff = createTwoFilesPatch(fileName, fileName, oldFileContent, newFileContent);

  const patchHeaderEnd = `--- ${fileName}\n+++ ${fileName}\n`;
  const headerEndIndex = unifiedDiff.indexOf(patchHeaderEnd);

  if (headerEndIndex >= 0) {
    unifiedDiff = unifiedDiff.slice(headerEndIndex + patchHeaderEnd.length);
  }

  if (unifiedDiff === '') {
    return undefined;
  }

  return unifiedDiff;
}

export function computeFileModifications(files: EditorDocuments, modifiedFiles: MapStore<Record<string, string>>) {
  const modifications: FileModifications = {};
  let hasModifiedFiles = false;

  Object.entries(modifiedFiles.get()).forEach(([filePath, originalContent]) => {
    const file = files[filePath];
    // if (file?.type !== 'file') return; // might need this

    const unifiedDiff = diffFiles(filePath, originalContent, file.content);
    if (!unifiedDiff) return;

    hasModifiedFiles = true;
    modifications[filePath] =
      unifiedDiff.length > file.content.length
        ? { type: 'file', content: file.content }
        : { type: 'diff', content: unifiedDiff };
  });

  return hasModifiedFiles ? modifications : undefined;
}

/**
 * Converts the unified diff to HTML.
 *
 * Example:
 *
 * ```html
 * <versori_file_modifications>
 * <diff path="/src/project/index.js">
 * - console.log('Hello, World!');
 * + console.log('Hello, Versori!');
 * </diff>
 * </versori_file_modifications>
 * ```
 */
export function fileModificationsToHTML(modifications: FileModifications) {
  const entries = Object.entries(modifications);
  if (entries.length === 0) return undefined;

  const result = [`<${MODIFICATIONS_TAG_NAME}>`];
  entries.forEach(([filePath, { type, content }]) => {
    result.push(`<${type} path=${JSON.stringify(filePath)}>`, content, `</${type}>`);
  });
  result.push(`</${MODIFICATIONS_TAG_NAME}>`);

  return result.join('\n');
}
